'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import {
  changeAccountRole,
  fetchAccountAccess,
  fetchMyInvites,
  inviteToAccount,
  leaveAccount,
  removeAccountAccess,
  respondToInvite,
  revokeInvite,
  transferAccountOwnership,
} from '@/service/account-access.api'
import { accountKeys } from '@/service/account.hook'
import {
  ChangeAccountRoleRequest,
  InviteToAccountRequest,
  RemoveAccountAccessRequest,
  RespondToInviteRequest,
  RevokeInviteRequest,
  Success,
  TransferAccountOwnershipRequest,
} from '@/types/generated/public_api'
import { ErrorResponse } from '@/types/service'

// Query keys
export const accountAccessKeys = {
  all: (userId?: string | null) => ['account-access', userId] as const,
  access: (userId?: string | null, accountId?: string) =>
    [...accountAccessKeys.all(userId), 'access', accountId] as const,
  myInvites: (userId?: string | null) => [...accountAccessKeys.all(userId), 'my-invites'] as const,
}

// No push channel exists for invites, so the other side's answer (an invite sent, accepted or
// declined elsewhere) only shows up by polling. React Query pauses the interval while the tab
// is hidden and refetches on focus, so the cost is one small request per interval from visible
// tabs only.
const INVITE_POLL_MS = 15 * 1000

// Members + pending invites of one account (admin only). While an invite is outstanding, poll
// so the invitee accepting moves them from "Pending invites" to members without a reopen.
export const useAccountAccess = (accountId?: string, options?: { enabled?: boolean }) => {
  const { user } = useAuth()
  return useQuery({
    queryKey: accountAccessKeys.access(user?.userId, accountId),
    queryFn: () => fetchAccountAccess(accountId!),
    enabled: Boolean(accountId) && (options?.enabled ?? true),
    staleTime: INVITE_POLL_MS,
    refetchInterval: (query) =>
      (query.state.data?.pendingInvites.length ?? 0) > 0 ? INVITE_POLL_MS : false,
  })
}

// Pending invites addressed to the logged-in user.
export const useMyInvites = (options?: { enabled?: boolean }) => {
  const { user, isAuthenticated } = useAuth()
  return useQuery({
    queryKey: accountAccessKeys.myInvites(user?.userId),
    queryFn: () => fetchMyInvites(),
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: INVITE_POLL_MS,
    refetchInterval: INVITE_POLL_MS,
  })
}

type MutationProps<TVariables> = {
  onSuccess?: (data: Success, variables: TVariables) => void
  onError?: (error: ErrorResponse, variables: TVariables) => void
}

export const useInviteToAccount = (props?: MutationProps<InviteToAccountRequest>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: InviteToAccountRequest) => inviteToAccount(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accountAccessKeys.access(user?.userId, variables.accountId),
      })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useRevokeInvite = (props?: MutationProps<RevokeInviteRequest>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: RevokeInviteRequest) => revokeInvite(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accountAccessKeys.access(user?.userId, variables.accountId),
      })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useRemoveAccountAccess = (props?: MutationProps<RemoveAccountAccessRequest>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: RemoveAccountAccessRequest) => removeAccountAccess(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accountAccessKeys.access(user?.userId, variables.accountId),
      })
      // Removing yourself behaves like leaving — the account disappears from your list
      if (variables.userId === user?.userId) {
        queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      }
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useChangeAccountRole = (props?: MutationProps<ChangeAccountRoleRequest>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: ChangeAccountRoleRequest) => changeAccountRole(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accountAccessKeys.access(user?.userId, variables.accountId),
      })
      // Changing your own role changes PublicAccount.role on the accounts list
      if (variables.userId === user?.userId) {
        queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      }
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useLeaveAccount = (props?: MutationProps<string>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => leaveAccount(accountId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useTransferAccountOwnership = (
  props?: MutationProps<TransferAccountOwnershipRequest>
) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: TransferAccountOwnershipRequest) => transferAccountOwnership(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accountAccessKeys.access(user?.userId, variables.accountId),
      })
      // The caller is demoted to read & write — refresh PublicAccount.role so
      // owner-only controls (manage, credentials, disable) disappear immediately.
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useRespondToInvite = (props?: MutationProps<RespondToInviteRequest>) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: RespondToInviteRequest) => respondToInvite(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountAccessKeys.myInvites(user?.userId) })
      // Accepting grants access — the account appears in get_user_accounts
      if (variables.accept) {
        queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      }
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}
